import React, { useState } from 'react';
import { useComic } from './ComicContext';
import { useAuth } from './AuthContext';
import { Check, X } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import balumIcon from '../../src/assets/icons/balum-icon-transparent.png';
import sairIcon from '../../src/assets/icons/_pub SAIR.png';
import catIcon from '../../src/assets/icons/_pub CAT.png';
import editIcon from '../../src/assets/icons/_pub EDIT.png';
import filtIcon from '../../src/assets/icons/_pub FILT.png';
import delIcon from '../../src/assets/icons/_pub DEL.png';
import criarIcon from '../../src/assets/icons/_pub CRIAR.png';
import './ComicListView.css';
import './LoadingSpinner.css';

const BACKEND_ORIGIN = '';

function getCoverUrl(comic) {
  const url = comic.pages?.['0']?.bgImage?.url;
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_ORIGIN}${url}`;
}

const ComicListView = ({ onSelectComic, onCreateNew }) => {
  const { comics, deleteComic, updateComicCategory, isLoadingComics, isCreatingComic } = useComic();
  const { logout } = useAuth();
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [isFiltering, setIsFiltering] = useState(false);
  const [editingFilterForComic, setEditingFilterForComic] = useState(null);
  const [editingFilterText, setEditingFilterText] = useState('');

  const handleDelete = (comicId) => {
    if (window.confirm('Tem certeza que deseja deletar este quadrinho?')) {
      deleteComic(comicId);
    }
  };

  const handleEdit = (comic) => {
    onSelectComic(comic.id);
  };

  const handleCreate = () => {
    onCreateNew();
  };

  const allCategories = [...new Set(comics
    .flatMap(comic => {
      const categoryStr = comic.metadata.category || '';
      if (!categoryStr.trim()) return [];
      return categoryStr.split(/[\s,]+/).filter(cat => cat !== '');
    })
  )];

  const handleFilterToggle = () => {
    setIsFiltering(!isFiltering);
    if (!isFiltering) setSelectedFilters([]);
  };

  const handleFilterSelect = (category) => {
    setSelectedFilters(prev =>
      prev.includes(category) ? prev.filter(f => f !== category) : [...prev, category]
    );
  };

  const handleRemoveFilter = (categoryToRemove) => {
    setSelectedFilters(prev => prev.filter(f => f !== categoryToRemove));
  };

  const handleClearFilters = () => setSelectedFilters([]);

  const handleStartEditFilter = (comic) => {
    setEditingFilterForComic(comic.id);
    setEditingFilterText(comic.metadata.category || '');
  };

  const handleCancelEditFilter = () => {
    setEditingFilterForComic(null);
    setEditingFilterText('');
  };

  const handleSaveFilter = (comicId) => {
    if (editingFilterText.trim() === '') {
      updateComicCategory(comicId, '');
    } else {
      const normalizedCategory = editingFilterText
        .split(/[\s,]+/).filter(cat => cat !== '').join(', ');
      updateComicCategory(comicId, normalizedCategory);
    }
    setEditingFilterForComic(null);
    setEditingFilterText('');
  };

  const filteredComics = isFiltering && selectedFilters.length > 0
    ? comics.filter(comic => {
        const comicCategory = comic.metadata.category || '';
        if (!comicCategory.trim()) return false;
        const comicCategories = comicCategory.split(/[\s,]+/)
          .filter(cat => cat !== '').map(cat => cat.toLowerCase());
        return selectedFilters.some(sel => comicCategories.includes(sel.toLowerCase()));
      })
    : comics;

  return (
    <div className="comic-list-container">
      <img src={balumIcon} alt="balum" className="top-bar-logo" />
      <button onClick={logout} className="logout-button" title="Sair">
        <img src={sairIcon} alt="Sair" style={{ width: 28, height: 28 }} />
      </button>
      <div className="comic-list-wrapper">

        {/* Header */}
        <div className="comic-list-header">
          <h1 className="comic-list-title">BIBLIOTECA DE HISTÓRIAS EM QUADRINHOS</h1>
          <p className="comic-list-subtitle">Selecione um título para editar ou crie um novo</p>
          {isLoadingComics && (
            <div className="loading-comics-indicator">
              <LoadingSpinner size={24} />
              <span className="loading-text">Carregando quadrinhos...</span>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="action-buttons-row">
          <button onClick={handleCreate} className="create-comic-button" disabled={isCreatingComic} title="Criar novo">
            {isCreatingComic ? <LoadingSpinner size={46} color="#fff" /> : <img src={criarIcon} alt="Criar" style={{ width: 46, height: 46 }} />}
          </button>
          <button onClick={handleFilterToggle} className="create-comic-button" title="Filtrar">
            <img src={filtIcon} alt="Filtrar" style={{ width: 46, height: 46 }} />
          </button>
        </div>

        {/* Filter Panel */}
        {isFiltering && (
          <div className="filter-panel">
            <div className="filter-panel-header">
              {selectedFilters.length > 0 && (
                <button onClick={handleClearFilters} className="clear-filters-button">Limpar seleção</button>
              )}
            </div>
            <div className="available-filters">
              {allCategories.length === 0 ? (
                <p className="no-filters-message">Nenhuma categoria definida nos quadrinhos.</p>
              ) : (
                <div className="filters-grid">
                  {allCategories.map(category => (
                    <button
                      key={category}
                      onClick={() => handleFilterSelect(category)}
                      className={`filter-tag ${selectedFilters.includes(category) ? 'selected' : ''}`}
                    >
                      {category}
                      {selectedFilters.includes(category) && <span className="filter-check">✓</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selectedFilters.length > 0 && (
              <div className="selected-filters-section">
                <div className="selected-filters-list">
                  {selectedFilters.map(category => (
                    <div key={category} className="selected-filter-item">
                      <span className="selected-filter-text">{category}</span>
                      <button onClick={() => handleRemoveFilter(category)} className="remove-filter-button">×</button>
                    </div>
                  ))}
                </div>
                <p className="selected-filters-count">
                  Mostrando {filteredComics.length} quadrinho{filteredComics.length !== 1 ? 's' : ''} com {selectedFilters.length} categoria{selectedFilters.length !== 1 ? 's' : ''} selecionada{selectedFilters.length !== 1 ? 's' : ''}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Comics Grid */}
        {filteredComics.length === 0 ? (
          <div className="empty-state">
            <p className="empty-state-text">
              {isFiltering && selectedFilters.length > 0
                ? 'Nenhum quadrinho encontrado com os filtros selecionados'
                : 'Nenhum quadrinho criado ainda'}
            </p>
            <button onClick={handleCreate} className="create-comic-button">
              <img src={criarIcon} alt="Criar" style={{ width: 46, height: 46 }} />
            </button>
          </div>
        ) : (
          <div className="comics-grid">
            {filteredComics.map((comic) => {
              const coverUrl = getCoverUrl(comic);
              const status = comic.metadata.status || 'development';
              const categories = (comic.metadata.category || '')
                .split(/[\s,]+/).filter(Boolean).join(', ');

              return (
                <div key={comic.id} className="comic-card">
                  {/* Cover image */}
                  {coverUrl && (
                    <div className="comic-cover-wrapper">
                      <img src={coverUrl} alt="capa" className="comic-cover-img" />
                    </div>
                  )}

                  {/* Info */}
                  <div className="comic-info">
                    <p className="comic-field-label">TÍTULO:</p>
                    <p className="comic-field-value comic-title">{comic.metadata.title}</p>

                    <p className="comic-field-label">AUTOR:</p>
                    <p className="comic-field-value">{comic.metadata.author}</p>

                    <p className="comic-field-value">No. de Páginas: {comic.metadata.totalPages}</p>

                    <div className="status-row">
                      <p className="comic-field-label">STATUS:</p>
                      <span className={`status-badge status-${status}`}>
                        {status === 'production' ? 'PUBLICADO' : <em>EM PRODUÇÃO</em>}
                      </span>
                    </div>

                    {categories && (
                      <>
                        <p className="comic-field-label" style={{ marginTop: '8px' }}>CATEGORIA:</p>
                        <p className="comic-field-value">{categories}</p>
                      </>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="action-buttons-list">
                    <button onClick={() => handleEdit(comic)} className="action-button edit">
                      <img src={editIcon} alt="Editar" style={{ width: 42, height: 42 }} />
                      <span className="button-label">EDITAR</span>
                    </button>
                    <button onClick={() => handleStartEditFilter(comic)} className="action-button filter-edit">
                      <img src={catIcon} alt="Categoria" style={{ width: 42, height: 42 }} />
                      <span className="button-label">CATEGORIA</span>
                    </button>
                    <button onClick={() => handleDelete(comic.id)} className="action-button delete">
                      <img src={delIcon} alt="Deletar" style={{ width: 42, height: 42 }} />
                      <span className="button-label">DELETAR</span>
                    </button>
                  </div>

                  {/* Filter edit inline */}
                  {editingFilterForComic === comic.id && (
                    <div className="filter-edit-interface">
                      <div className="filter-edit-input-group">
                        <input
                          type="text"
                          value={editingFilterText}
                          onChange={(e) => setEditingFilterText(e.target.value)}
                          className="filter-edit-input"
                          placeholder="Categorias separadas por vírgula"
                          onKeyPress={(e) => e.key === 'Enter' && handleSaveFilter(comic.id)}
                          autoFocus
                        />
                      </div>
                      <div className="filter-edit-buttons">
                        <button onClick={() => handleSaveFilter(comic.id)} className="action-button save-filter">
                          <div className="button-circle save"><Check size={18} color="#fff" /></div>
                          <span className="button-label">SALVAR</span>
                        </button>
                        <button onClick={handleCancelEditFilter} className="action-button cancel-filter">
                          <div className="button-circle cancel"><X size={18} color="#fff" /></div>
                          <span className="button-label">CANCELAR</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComicListView;
